import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { InferType } from "yup";

import type {
  BaseFormTemplate,
  ExtendedThalamosUser,
  FormContextData,
} from "@thalamos/aspire-common";
import {
  baseTemplates,
  capitalize,
  getFullFormName,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import type {
  newFormAssigneeData,
  NewResultWithoutAccreditation,
} from "../../../components/FormAssignee/formAssigneeTypes.js";
import { NewFormAssigneeDialog } from "../../../components/FormAssignee/NewFormAssigneeDialog.js";
import { routeFns } from "../../../routePaths.js";
import { admissionFormContextLogic } from "../helpers/formContextLogic.js";
import { InterviewLinkingStep } from "./InterviewLinkingStep.js";
import { actions, getTemplate } from "./requestWorkItemActions.js";
import {
  Banner,
  BannerList,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";

const guestAccreditationOptions = [
  {
    label: "Registered medical practitioner",
    value: "registered-medical-practitioner",
  },
  {
    label: "Approved mental health professional",
    value: "approved-mental-health-professional",
  },
  {
    label: "Nurse/Receiving Officer",
    value: "nurse-receiving-officer",
  },
  {
    label: "Mental health act administrator",
    value: "mental-health-act-administrator",
  },
  {
    label: "Other",
    value: "other",
  },
];

const completionOption = {
  label: "Complete",
  value: "complete",
};

const admissionBannerLogic = (
  user: ExtendedThalamosUser,
  formContext: Omit<FormContextData, "patient">,
  fullFormName: string | null,
  formTemplate: BaseFormTemplate,
  requestType: string,
) => {
  const {
    needsPart2Forms,
    admissionHasCompletedApplication,
    admissionHasCompletedRecordOfDetention,
    canScrutiniseAdmission,
  } = admissionFormContextLogic(formContext, user);

  const formSectionAndCategory = formTemplate
    ? `${formTemplate?.section} ${capitalize(formTemplate?.category)}`
    : "";

  if (
    admissionHasCompletedRecordOfDetention &&
    user.sessionContext?.teamType === "mha"
  ) {
    return {
      value: `Send Section ${formSectionAndCategory} Forms to another user or team. `,
    };
  }

  if (canScrutiniseAdmission) {
    return {
      value: `Send Section ${formSectionAndCategory} Forms to another user or team. `,
    };
  }

  // If finalised
  if (
    admissionHasCompletedApplication &&
    admissionHasCompletedApplication.status === "finalised"
  ) {
    const applicationTemplate = baseTemplates.find(
      (t) =>
        t.id === admissionHasCompletedApplication.formTemplate.id &&
        t.version === admissionHasCompletedApplication.formTemplate.version,
    )!;
    return {
      value: `Send Section ${applicationTemplate.section} Admission Forms to another user or team.`,
    };
  }

  if (!!needsPart2Forms.length && requestType === "complete") {
    return {
      guestAccreditationOptions: [completionOption],
      value: `Send ${fullFormName} to another user or team to complete Part 2.`,
      info: "Part 2 to be completed by a registered medical practitioner.",
    };
  }

  if (requestType === "medical-recommendation") {
    return {
      guestAccreditationOptions: [
        guestAccreditationOptions.find(
          (o) => o.value === "registered-medical-practitioner",
        )!,
      ],
      value:
        "Invite a registered medical practitioner to work on this assessment. ",
      info: "The registered medical practitioner will choose whether to make a medical recommendation based on their assessment.",
    };
  }

  if (requestType === "application") {
    return {
      guestAccreditationOptions: [
        guestAccreditationOptions.find(
          (o) => o.value === "approved-mental-health-professional",
        )!,
      ],
      value:
        "Invite an approved mental health professional to work on this assessment.",
      info: "The approved mental health professional will choose whether to make an application based on their assessment.",
    };
  }

  return {
    guestAccreditationOptions,
    value: "Invite another user(s) or team to work on this assessment.",
  };
};

export const bannerLogic = (
  user: ExtendedThalamosUser,
  formContext: Omit<FormContextData, "patient">,
  fullFormName: string | null,
  formTemplate: BaseFormTemplate,
  requestType: string,
) => {
  if (formContext.type === "admission") {
    return admissionBannerLogic(
      user,
      formContext,
      fullFormName,
      formTemplate,
      requestType,
    );
  }

  if (requestType === "complete") {
    return {
      guestAccreditationOptions: [completionOption],
      value: `Invite another user or team to work on this form.`,
    };
  }

  if (requestType === "finalise" && formContext.type === "police-incident") {
    return {
      guestAccreditationOptions: [
        {
          label: "Mental health act administrator",
          value: "mental-health-act-administrator",
        },
      ],
      value: `Invite another user or team to work on this form.`,
    };
  }

  // If the invited user is a guest and it's not a 'finalise' request
  // then ask the inviting user to select an accreditation type
  if (formContext.type === "police-incident") {
    return {
      guestAccreditationOptions: [
        {
          label: "Mental health act administrator",
          value: "mental-health-act-administrator",
        },
        {
          label: "Other",
          value: "other",
        },
      ],
      value: `Invite another user or team to work on this form.`,
    };
  }

  if (formContext.forms.some((f) => f.status === "finalised")) {
    return {
      value: `Send ${fullFormName} to another user or team`,
    };
  }

  return { value: `Invite another user or team to work on this form.` };
};

export function RequestWorkItemDialog({
  closeFn,
  formContext,
  user,
  reloadFormContext,
  formId,
  title,
  infoMessage,
  openS12PaymentRequest,
  s12OutcomeType,
  s12FormId,
  linkInterview,
}: {
  closeFn: () => void;
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  reloadFormContext: () => void;
  formId?: string;
  title?: string;
  infoMessage?: string;
  /** If true, after sending, redirect to S12 payment request dialog */
  openS12PaymentRequest?: boolean;
  /** Outcome type for S12 payment request */
  s12OutcomeType?: string;
  /** Form ID for S12 payment request */
  s12FormId?: string;
  /** If true, show interview-linking picker before send button (drFinder flow only) */
  linkInterview?: boolean;
}) {
  const [searchResults, setSearchResults] = useState<
    NewResultWithoutAccreditation[]
  >([]);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const qs = new URLSearchParams(window.location.search);
  const requestType = qs.get("requestType");

  // ----- Interview linking state -----
  // When linkInterview is true, we show an interview picker inline before the
  // Send button. The selected interview ID is passed to SendRequestButton which
  // calls addParticipant after sending work items.
  const [selectedInterviewId, setSelectedInterviewId] = useState<string | null>(
    null,
  );

  const availableActions = actions(formContext, user);

  const getForms = () => {
    const activeTeamworkWorkItem = formContext.activeTeamworkWorkItem;
    if (activeTeamworkWorkItem?.formContextType === "admission") {
      return activeTeamworkWorkItem.forms.find(
        (f) => f.formTemplate.id !== "mha-h3",
      );
    }
    return activeTeamworkWorkItem?.forms[0];
  };

  const getForm = getForms();

  const formTemplate = baseTemplates.find(
    (t) => t.id === getForm?.formTemplate.id,
  );

  const fullFormName = getFullFormName(formTemplate as BaseFormTemplate | null);

  useEffect(() => {
    if (availableActions.length === 1) {
      navigate(
        routeFns.formContextPageSendRequest(
          formContext.id,
          formContext.patientId,
          { requestType: "share" },
        ),
        { replace: true },
      );
    }
  }, []);

  const [recipients, setRecipients] = useState<
    InferType<typeof newFormAssigneeData>[]
  >([]);

  const { value, info, guestAccreditationOptions } = bannerLogic(
    user,
    formContext,
    fullFormName,
    formTemplate!,
    requestType!,
  );

  return (
    <PopupDialog
      maxWidth="lg"
      open={true}
      onClose={closeFn}
      testId="request-work-item-dialog"
    >
      <PopupDialogTitle
        titleText={
          linkInterview
            ? t("common.inviteDoctor")
            : formContext.type === "admission"
              ? t("common.sendInvite")
              : formContext.type === "soad-assessment"
                ? t("common.sendAssessment")
                : t("common.sendForm")
        }
        closeDialog={() => {
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
            { replace: true },
          );
        }}
        backAction={
          linkInterview
            ? () => {
                navigate(
                  routeFns.formContextInterviews(
                    formContext.id,
                    formContext.patientId,
                  ),
                  { replace: true },
                );
              }
            : undefined
        }
      />

      <Box>
        <Box sx={{ mb: 4 }}>
          <Banner
            title={(title ? title : value)!}
            info={(title ? infoMessage : info)!}
            singleLine
            bannerType={BannerList.INFO}
          />
        </Box>

        <NewFormAssigneeDialog
          recipients={recipients}
          setRecipients={setRecipients}
          formId={formId}
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          availableActions={availableActions}
          searchAssignmentOptions={async (formDetails, searchValue) => {
            return api.formTemplates.searchAssignmentOptions({
              id: formDetails.template.id,
              version: formDetails.template.version,
              query: searchValue,
              part: formDetails.part,
            });
          }}
          formDetails={getTemplate(formContext)}
          userId={user.id}
          userEmail={user.email}
          userTeamId={user.sessionContext?.teamId}
          userDataControllerId={user.sessionContext?.dataControllerId}
          userDataControllerName={user.sessionContext?.dataControllerName}
          dataControllerWarningEnabled={
            // Exempt guest, soad, and independent sender contexts from the
            // data controller warning — they operate outside the normal
            // data-controller model so the banner is not meaningful.
            user.sessionContext?.teamType !== "guest" &&
            user.sessionContext?.teamType !== "soad" &&
            user.sessionContext?.teamType !== "independent" &&
            (user.sessionOrganisationConfiguration
              ?.dataControllerWarningEnabled ??
              false)
          }
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          onSave={async (
            _formData: InferType<typeof newFormAssigneeData>,
          ) => {}}
          navigate={navigate}
          guestAccreditationOptions={guestAccreditationOptions}
          openS12PaymentRequest={openS12PaymentRequest}
          s12OutcomeType={s12OutcomeType}
          s12FormId={s12FormId}
          preSubmitContent={
            linkInterview ? (
              <InterviewLinkingStep
                formContextId={formContext.id}
                selectedInterviewId={selectedInterviewId}
                onSelectedInterviewIdChange={setSelectedInterviewId}
              />
            ) : undefined
          }
          selectedInterviewId={linkInterview ? selectedInterviewId : undefined}
        />
      </Box>
    </PopupDialog>
  );
}
